import React, { memo, useCallback, useState } from 'react';
import { Box, Checkbox, IconButton, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * Virtual DataGrid header with sorting and selection
 */
const VirtualDataGridHeader = memo(({
  columns,
  checkboxSelection,
  isAllSelected,
  isIndeterminate,
  onSelectAll,
  sortModel,
  onSortModelChange,
  onColumnResize,
  height
}) => {
  const [resizing, setResizing] = useState(null);

  // Handle select all checkbox
  const handleSelectAllChange = useCallback((e) => {
    onSelectAll(e.target.checked);
  }, [onSelectAll]);

  // Handle column sort
  const handleSort = useCallback((field) => {
    if (!onSortModelChange) return;

    const currentSort = sortModel?.find(s => s.field === field);
    let newSortModel;

    if (!currentSort) {
      // Add ascending sort
      newSortModel = [{ field, sort: 'asc' }];
    } else if (currentSort.sort === 'asc') {
      // Change to descending
      newSortModel = [{ field, sort: 'desc' }];
    } else {
      // Remove sort
      newSortModel = [];
    }

    onSortModelChange(newSortModel);
  }, [sortModel, onSortModelChange]);

  return (
    <Box
      sx={{
        height: `${height}px`,
        minHeight: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'grey.300',
        borderBottom: '2px solid rgba(224, 224, 224, 1)',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: 'text.primary'
      }}
    >
      {/* Checkbox column header */}
      {checkboxSelection && (
        <Box
          sx={{
            width: 52,
            minWidth: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pl: 1
          }}
        >
          <Checkbox
            size="small"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={handleSelectAllChange}
            inputProps={{ 'aria-label': 'Select all rows' }}
          />
        </Box>
      )}

      {/* Column headers */}
      {columns.map((column) => (
        <VirtualDataGridHeaderCell
          key={column.field}
          column={column}
          sortModel={sortModel}
          onSort={handleSort}
          onResize={onColumnResize}
          height={height}
        />
      ))}
    </Box>
  );
});

/**
 * Individual header cell with sorting and resizing
 */
const VirtualDataGridHeaderCell = memo(({
  column,
  sortModel,
  onSort,
  onResize,
  height
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const currentSort = sortModel?.find(s => s.field === column.field);

  // Handle sort click
  const handleSortClick = useCallback(() => {
    if (column.sortable !== false) {
      onSort(column.field);
    }
  }, [column.field, column.sortable, onSort]);

  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(column.width);

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(column.minWidth || 80, startWidth + diff);
      onResize(column.field, newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [column.field, column.width, column.minWidth, onResize, startX, startWidth]);

  // Render sort icon
  const sortIcon = currentSort ? (
    currentSort.sort === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
  ) : null;

  // Flex columns grow to fill remaining horizontal space; others are fixed width
  const sizing = column.flex
    ? { flex: column.flex, minWidth: column.minWidth || 0 }
    : {
        width: column.width,
        minWidth: column.minWidth || column.width,
        maxWidth: column.maxWidth || column.width,
      };

  return (
    <Box
      sx={{
        ...sizing,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        px: 2,
        borderRight: '1px solid rgba(224, 224, 224, 1)',
        cursor: column.sortable !== false ? 'pointer' : 'default',
        userSelect: 'none',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        },
        '&:last-child': {
          borderRight: 'none'
        }
      }}
      onClick={handleSortClick}
    >
      {/* Header text */}
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          color: 'text.primary'
        }}
      >
        {column.headerName || column.field}
      </Typography>

      {/* Sort icon */}
      {sortIcon && (
        <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
          {sortIcon}
        </Box>
      )}

      {/* Resize handle */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 4,
          cursor: 'col-resize',
          backgroundColor: isResizing ? 'primary.main' : 'transparent',
          '&:hover': {
            backgroundColor: 'primary.light'
          }
        }}
        onMouseDown={handleResizeStart}
      />
    </Box>
  );
});

VirtualDataGridHeader.displayName = 'VirtualDataGridHeader';
VirtualDataGridHeaderCell.displayName = 'VirtualDataGridHeaderCell';

export default VirtualDataGridHeader;